/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { CalendarDate } from '@internationalized/date';

import {
    useBreakpoint,
    StyledModal,
    InlinePopup,
    Icon,
    Button,
    Alert,
    ButtonGroup,
    pluralize,
} from 'sarsaparilla';

import { searchDate } from 'ui-search/src/utils/dateUtils';

import { FlexDatesPopupContent } from './FlexDatesPopupContent';
import { MAX_MONTHS_AHEAD } from './FlexDates';
import type { FiltersProps, MonthsOptionsProps } from './FlexDates.types';

interface FlexDatesPopupProps {
    filters: FiltersProps;
    setIsPortalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    triggerRef: React.RefObject<HTMLButtonElement>;
    onFiltersChange: (v: FiltersProps) => void;
    monthsLimit?: number;
    monthsOptions: Array<MonthsOptionsProps>;
}

// The "portal" is always open because this component is only rendered when it is true.
// This is controlled by the parent component.
const isPortalOpen = true;

export function FlexDatesPopup({
    setIsPortalOpen,
    filters,
    triggerRef,
    onFiltersChange,
    monthsLimit,
    monthsOptions,
}: FlexDatesPopupProps) {
    const [error, setError] = React.useState<string>('');
    const [tab, setTab] = React.useState<'choose-dates' | 'flexible-booking'>(() => {
        if (filters?.duration) {
            return 'flexible-booking';
        }

        return 'choose-dates';
    });
    const [checked, setChecked] = React.useState<Array<string>>([]);
    const [duration, setDuration] = React.useState<string>('1');

    const [dates, setDates] = React.useState<{
        startDate?: CalendarDate | null;
        endDate?: CalendarDate | null;
    }>(() => {
        if (filters?.startDate && filters?.endDate) {
            return { startDate: filters.startDate, endDate: filters.endDate };
        }
        return { startDate: null, endDate: null };
    });

    const [startRange, setStartRange] = React.useState<string>(() => {
        if (filters.startDate && filters.endDate && filters.startRange) {
            return filters.startRange;
        }

        return '';
    });

    const [endRange, setEndRange] = React.useState<string>(() => {
        if (filters.startDate && filters.endDate && filters.endRange) {
            return filters.endRange;
        }

        return '';
    });

    const portalRef = React.useRef<HTMLDivElement>(null);
    const { isSmAndDown } = useBreakpoint();
    const isSearchPage = window.location.pathname.includes('search');

    const isClearButtonActive = React.useCallback(() => {
        return (
            document.getElementById('flex-dates-input__clear') === document.activeElement
        );
    }, []);

    const isCloseButtonActive = React.useCallback(() => {
        return (
            document.getElementById('flex-dates-close-button') === document.activeElement
        );
    }, []);

    const handleBlur = () => {
        if (document.activeElement === triggerRef.current || isClearButtonActive()) {
            return;
        }

        let payload: FiltersProps = {};

        if (tab === 'flexible-booking') {
            if (checked.length !== 0) {
                payload = { months: checked, duration };
            }
        } else if (tab === 'choose-dates') {
            if (dates?.startDate || dates?.endDate) {
                payload = { startDate: dates?.startDate, endDate: dates?.endDate };
            }
            if (startRange) {
                payload.startRange = startRange;
            }
            if (endRange) {
                payload.endRange = endRange;
            }
        }

        if (
            portalRef.current?.contains(document.activeElement) &&
            !isCloseButtonActive()
        ) {
            return;
        }

        setIsPortalOpen(false);
        triggerRef?.current?.focus();
        setTab('choose-dates');
    };

    const handleApplyFilters = (): void => {
        let payload = {};
        if (tab === 'flexible-booking') {
            if (checked.length === 0) {
                setError(
                    'You need to select at least one month to search for availability.'
                );
                return;
            }
            payload = { months: checked, duration };
        }
        if (tab === 'choose-dates') {
            if (!dates?.startDate || !dates?.endDate) {
                setError(
                    'You need to select a Check In and a Check Out date to search for availability.'
                );
                return;
            }
            if (dates.startDate && dates.startDate.compare(dates.endDate) > 0) {
                setError('Check In date cannot be after the Check Out date.');
                return;
            }
            const today = searchDate(new Date()); // Get today's date

            if (dates?.startDate && dates.startDate.compare(today) < 0) {
                setError('Start Date cannot be before today.');
                return;
            }
            const maxDate = today.add({ months: MAX_MONTHS_AHEAD });
            if (
                (dates?.endDate && dates.endDate.compare(maxDate) > 0) ||
                (dates?.startDate && dates.startDate.compare(maxDate) > 0)
            ) {
                setError('Dates must be within 12 months from today.');
                return;
            }
            payload = {
                startRange,
                endRange,
                startDate: dates?.startDate,
                endDate: dates?.endDate,
            };
        }
        onFiltersChange({
            ...payload,
        });
        setIsPortalOpen(false);
    };

    const handleClear = (): void => {
        setDates({ startDate: null, endDate: null });
        setDuration('1');
        setStartRange('');
        setEndRange('');
        setChecked([]);
    };

    const handleReset = () => {
        handleClear();
        onFiltersChange({});
        setIsPortalOpen(false);
    };

    /**
     * Set up keyup event listener to handle escape and tab key events
     */
    React.useEffect(() => {
        const handleKeyup = (event: KeyboardEvent): void => {
            const key = event.key || event.which;

            if (key === 'Escape' || key === 'Esc' || key === 27) {
                event.preventDefault();
                setIsPortalOpen(false);
                const isButtonRefWorking = !!triggerRef?.current?.focus;

                if (isButtonRefWorking) {
                    triggerRef?.current?.focus();
                }
            }

            if (key === 'Tab' || key === 9) {
                const _flexDatesWrapper =
                    document?.getElementById('flex-dates-container');
                if (
                    tab === 'choose-dates' &&
                    dates.startDate &&
                    dates.endDate &&
                    _flexDatesWrapper
                ) {
                    if (!_flexDatesWrapper.contains(document.activeElement)) {
                        document.getElementById('calendar-button-handler')?.focus();
                    }
                    return;
                }

                if (isClearButtonActive()) {
                    return;
                }

                const isWrapperRefWorking = !!portalRef?.current?.contains;

                if (
                    isWrapperRefWorking &&
                    !portalRef.current?.contains(document.activeElement)
                ) {
                    triggerRef?.current?.focus();
                    setIsPortalOpen(false);
                }
            }
        };

        document.addEventListener('keyup', handleKeyup);

        return () => {
            document.removeEventListener('keyup', handleKeyup);
        };
    }, [dates, tab, setIsPortalOpen, triggerRef, isClearButtonActive]);

    const generateGaProps = () => {
        const clickTagCategory = isSearchPage ? 'Search' : 'Homepage';
        if (tab === 'choose-dates') {
            let startTagAction = '';
            let endTagAction = '';
            let clickTagLabel = 'Selected Dates';
            if (dates?.startDate) {
                startTagAction = `Check-in: ${searchDate(dates?.startDate).format('LL')}`;
            }
            if (dates?.endDate) {
                endTagAction = `Check-out: ${searchDate(dates?.endDate).format('LL')}`;
            }
            if (startRange) {
                startTagAction = `${startTagAction} plus or minus ${startRange} ${pluralize(
                    Number(startRange),
                    'Day',
                    'Days'
                )}`;
                clickTagLabel = 'Selected Dates with plus or minus days';
            }
            if (endRange) {
                endTagAction = `${endTagAction} plus or minus ${endRange} ${pluralize(
                    Number(endRange),
                    'Day',
                    'Days'
                )}`;
                clickTagLabel = 'Selected Dates with plus or minus days';
            }
            return {
                clickTagCategory,
                clickTagAction: `${startTagAction}, ${endTagAction}`,
                clickTagLabel,
            };
        }
        const clickTagAction = `${duration} ${pluralize(
            Number(duration),
            'Day',
            'Days'
        )} Within ${checked.length} Selected ${pluralize(
            Number(checked.length),
            'Month',
            'Months'
        )}`;
        return {
            clickTagCategory,
            clickTagAction,
            clickTagLabel: 'Flexible Booking',
        };
    };
    const gaProps = generateGaProps();

    const shouldCloseOnInteractOutside = (el: Element) => {
        if (portalRef.current?.contains(el)) return false;
        if (triggerRef.current?.contains(el)) return true;

        (document.activeElement as HTMLElement)?.blur(); // reset the activeElement so the handleBlur function works as expected.
        return true;
    };

    return isSmAndDown ? (
        <StyledModal
            size="md"
            isOpen={isPortalOpen}
            shouldShowCloseButton={false}
            heading=""
            onRequestClose={() => {}}
            overlayClassName="flex-dates-mobile-modal"
        >
            <div className="flex-dates-mobile">
                <Button
                    aria-label="Close"
                    className="flex-dates-mobile-close"
                    appearance="link"
                    size="sm"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        if (e?.preventDefault) {
                            e.preventDefault();
                        }
                        handleBlur();
                    }}
                    onFocus={(e: React.FocusEvent<HTMLButtonElement>) => {
                        if (e?.preventDefault) {
                            e.preventDefault();
                        }
                    }}
                >
                    <Icon iconName="close-circle-outline" />
                </Button>
                {error ? (
                    <div className="pb-2">
                        <Alert type="error">{error}</Alert>
                    </div>
                ) : null}

                <FlexDatesPopupContent
                    ref={null}
                    dates={dates}
                    setDates={setDates}
                    setDuration={setDuration}
                    duration={duration}
                    checked={checked}
                    setChecked={setChecked}
                    handleApplyFilters={handleApplyFilters}
                    filters={filters}
                    tab={tab}
                    setTab={setTab}
                    isMobile={isSmAndDown}
                    monthsOptions={monthsOptions}
                    error={error}
                    setError={setError}
                    onClear={handleReset}
                    onClose={handleBlur}
                    startRange={startRange}
                    setStartRange={setStartRange}
                    setEndRange={setEndRange}
                    endRange={endRange}
                    generateGaProps={generateGaProps}
                />
            </div>
            <div className="flex-dates-mobile__actions">
                <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                    <Button appearance="link" onClick={handleReset}>
                        Clear Selection
                    </Button>
                    <Button onClick={handleApplyFilters} {...gaProps}>
                        Apply Dates
                    </Button>
                </ButtonGroup>
            </div>
        </StyledModal>
    ) : (
        <InlinePopup
            isOpen={!isSmAndDown}
            close={handleBlur}
            shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
            hideClose
        >
            <FlexDatesPopupContent
                ref={portalRef}
                dates={dates}
                setDates={setDates}
                setDuration={setDuration}
                duration={duration}
                checked={checked}
                setChecked={setChecked}
                handleApplyFilters={handleApplyFilters}
                filters={filters}
                tab={tab}
                onClear={handleReset}
                setTab={setTab}
                monthsLimit={monthsLimit}
                isMobile={isSmAndDown}
                monthsOptions={monthsOptions}
                error={error}
                setError={setError}
                onClose={handleBlur}
                startRange={startRange}
                setStartRange={setStartRange}
                setEndRange={setEndRange}
                endRange={endRange}
                generateGaProps={generateGaProps}
            />
        </InlinePopup>
    );
}
